/**
 * Part of Speech Labels Utility
 * Provides localized labels for POS abbreviations
 */

import i18n from '../i18n';

export type SupportedLocale = 'zh-TW' | 'zh-CN' | 'en' | 'ja' | 'ko' | 'vi' | 'th' | 'ms' | 'id';

// POS labels by locale
const posLabels: Record<SupportedLocale, Record<string, string>> = {
  'zh-TW': {
    'n.': '名詞',
    'v.': '動詞',
    'vt.': '及物動詞',
    'vi.': '不及物動詞',
    'a.': '形容詞',
    'adj.': '形容詞',
    'ad.': '副詞',
    'adv.': '副詞',
    'prep.': '介詞',
    'conj.': '連接詞',
    'pron.': '代名詞',
    'int.': '感嘆詞',
    'interj.': '感嘆詞',
    'art.': '冠詞',
    'num.': '數詞',
    'abbr.': '縮寫',
    'aux.': '助動詞',
    'pl.': '複數',
    'phr.': '片語',
    'v.phr.': '動詞片語',
    'n.phr.': '名詞片語',
    'adj.phr.': '形容詞片語',
    'adv.phr.': '副詞片語',
    'prep.phr.': '介詞片語',
  },
  'zh-CN': {
    'n.': '名词',
    'v.': '动词',
    'vt.': '及物动词',
    'vi.': '不及物动词',
    'a.': '形容词',
    'adj.': '形容词',
    'ad.': '副词',
    'adv.': '副词',
    'prep.': '介词',
    'conj.': '连接词',
    'pron.': '代名词',
    'int.': '感叹词',
    'interj.': '感叹词',
    'art.': '冠词',
    'num.': '数词',
    'abbr.': '缩写',
    'aux.': '助动词',
    'pl.': '复数',
    'phr.': '短语',
    'v.phr.': '动词短语',
    'n.phr.': '名词短语',
    'adj.phr.': '形容词短语',
    'adv.phr.': '副词短语',
    'prep.phr.': '介词短语',
  },
  'en': {
    'n.': 'noun',
    'v.': 'verb',
    'vt.': 'transitive verb',
    'vi.': 'intransitive verb',
    'a.': 'adjective',
    'adj.': 'adjective',
    'ad.': 'adverb',
    'adv.': 'adverb',
    'prep.': 'preposition',
    'conj.': 'conjunction',
    'pron.': 'pronoun',
    'int.': 'interjection',
    'interj.': 'interjection',
    'art.': 'article',
    'num.': 'numeral',
    'abbr.': 'abbreviation',
    'aux.': 'auxiliary verb',
    'pl.': 'plural',
    'phr.': 'phrase',
    'v.phr.': 'verbal phrase',
    'n.phr.': 'noun phrase',
    'adj.phr.': 'adjective phrase',
    'adv.phr.': 'adverb phrase',
    'prep.phr.': 'prepositional phrase',
  },
  'ja': {
    'n.': '名詞',
    'v.': '動詞',
    'vt.': '他動詞',
    'vi.': '自動詞',
    'a.': '形容詞',
    'adj.': '形容詞',
    'ad.': '副詞',
    'adv.': '副詞',
    'prep.': '前置詞',
    'conj.': '接続詞',
    'pron.': '代名詞',
    'int.': '感動詞',
    'interj.': '感動詞',
    'art.': '冠詞',
    'num.': '数詞',
    'abbr.': '略語',
    'aux.': '助動詞',
    'pl.': '複数形',
    'phr.': '句',
    'v.phr.': '動詞句',
    'n.phr.': '名詞句',
    'adj.phr.': '形容詞句',
    'adv.phr.': '副詞句',
    'prep.phr.': '前置詞句',
  },
  'ko': {
    'n.': '명사',
    'v.': '동사',
    'vt.': '타동사',
    'vi.': '자동사',
    'a.': '형용사',
    'adj.': '형용사',
    'ad.': '부사',
    'adv.': '부사',
    'prep.': '전치사',
    'conj.': '접속사',
    'pron.': '대명사',
    'int.': '감탄사',
    'interj.': '감탄사',
    'art.': '관사',
    'num.': '수사',
    'abbr.': '약어',
    'aux.': '조동사',
    'pl.': '복수',
    'phr.': '구',
    'v.phr.': '동사구',
    'n.phr.': '명사구',
    'adj.phr.': '형용사구',
    'adv.phr.': '부사구',
    'prep.phr.': '전치사구',
  },
  'vi': {
    'n.': 'danh từ',
    'v.': 'động từ',
    'vt.': 'ngoại động từ',
    'vi.': 'nội động từ',
    'a.': 'tính từ',
    'adj.': 'tính từ',
    'ad.': 'trạng từ',
    'adv.': 'trạng từ',
    'prep.': 'giới từ',
    'conj.': 'liên từ',
    'pron.': 'đại từ',
    'int.': 'thán từ',
    'interj.': 'thán từ',
    'art.': 'mạo từ',
    'num.': 'số từ',
    'abbr.': 'viết tắt',
    'aux.': 'trợ động từ',
    'pl.': 'số nhiều',
    'phr.': 'cụm từ',
    'v.phr.': 'cụm động từ',
    'n.phr.': 'cụm danh từ',
    'adj.phr.': 'cụm tính từ',
    'adv.phr.': 'cụm trạng từ',
    'prep.phr.': 'cụm giới từ',
  },
  'th': {
    'n.': 'คำนาม',
    'v.': 'คำกริยา',
    'vt.': 'สกรรมกริยา',
    'vi.': 'อกรรมกริยา',
    'a.': 'คำคุณศัพท์',
    'adj.': 'คำคุณศัพท์',
    'ad.': 'คำวิเศษณ์',
    'adv.': 'คำวิเศษณ์',
    'prep.': 'คำบุพบท',
    'conj.': 'คำสันธาน',
    'pron.': 'คำสรรพนาม',
    'int.': 'คำอุทาน',
    'interj.': 'คำอุทาน',
    'art.': 'คำนำหน้านาม',
    'num.': 'คำเลข',
    'abbr.': 'คำย่อ',
    'aux.': 'กริยาช่วย',
    'pl.': 'พหูพจน์',
    'phr.': 'วลี',
    'v.phr.': 'วลีกริยา',
    'n.phr.': 'วลีนาม',
    'adj.phr.': 'วลีคุณศัพท์',
    'adv.phr.': 'วลีวิเศษณ์',
    'prep.phr.': 'วลีบุพบท',
  },
  'ms': {
    'n.': 'kata nama',
    'v.': 'kata kerja',
    'vt.': 'kata kerja transitif',
    'vi.': 'kata kerja tak transitif',
    'a.': 'kata sifat',
    'adj.': 'kata sifat',
    'ad.': 'kata keterangan',
    'adv.': 'kata keterangan',
    'prep.': 'kata sendi',
    'conj.': 'kata hubung',
    'pron.': 'kata ganti nama',
    'int.': 'kata seru',
    'interj.': 'kata seru',
    'art.': 'kata sandang',
    'num.': 'kata bilangan',
    'abbr.': 'singkatan',
    'aux.': 'kata kerja bantu',
    'pl.': 'jamak',
    'phr.': 'frasa',
    'v.phr.': 'frasa kerja',
    'n.phr.': 'frasa nama',
    'adj.phr.': 'frasa sifat',
    'adv.phr.': 'frasa keterangan',
    'prep.phr.': 'frasa sendi',
  },
  'id': {
    'n.': 'kata benda',
    'v.': 'kata kerja',
    'vt.': 'kata kerja transitif',
    'vi.': 'kata kerja intransitif',
    'a.': 'kata sifat',
    'adj.': 'kata sifat',
    'ad.': 'kata keterangan',
    'adv.': 'kata keterangan',
    'prep.': 'kata depan',
    'conj.': 'kata penghubung',
    'pron.': 'kata ganti',
    'int.': 'kata seru',
    'interj.': 'kata seru',
    'art.': 'kata sandang',
    'num.': 'kata bilangan',
    'abbr.': 'singkatan',
    'aux.': 'kata kerja bantu',
    'pl.': 'jamak',
    'phr.': 'frasa',
    'v.phr.': 'frasa kerja',
    'n.phr.': 'frasa benda',
    'adj.phr.': 'frasa sifat',
    'adv.phr.': 'frasa keterangan',
    'prep.phr.': 'frasa depan',
  },
};

/**
 * Get current locale dynamically from i18n
 */
export function getCurrentLocale(): SupportedLocale {
  const lang = i18n.language;
  if (lang && lang in posLabels) {
    return lang as SupportedLocale;
  }
  return 'zh-TW'; // Default
}

/**
 * Malformed/variant POS strings emitted by the AI example generator, folded to
 * the canonical (space-free) keys used in `posLabels`. Keys here are already
 * lowercased, whitespace-stripped, and period-terminated.
 */
const POS_ALIASES: Record<string, string> = {
  'phrase.': 'phr.',
  'phr.v.': 'v.phr.',
  'verbphrase.': 'v.phr.',
  'nounphrase.': 'n.phr.',
  'adjphrase.': 'adj.phr.',
  'advphrase.': 'adv.phr.',
  'prepphrase.': 'prep.phr.',
};

/**
 * Format POS abbreviation to localized label
 * @param pos - Part of speech abbreviation (e.g., "vt.", "n.", "adj", "v. phr.")
 * @param locale - Target locale (optional, uses current i18n locale if not specified)
 * @returns Localized POS label
 */
export function formatPOS(pos: string, locale?: SupportedLocale): string {
  if (!pos) return '';

  const targetLocale = locale || getCurrentLocale();

  // Normalize: lowercase + strip ALL inner whitespace so "v. phr." matches the
  // map key "v.phr.", ensure a trailing period, then fold known malformed
  // variants ("phr. v.", "verb phrase") to a canonical key.
  let key = pos.toLowerCase().replace(/\s+/g, '');
  if (!key.endsWith('.')) key += '.';
  key = POS_ALIASES[key] || key;

  return posLabels[targetLocale]?.[key] || pos;
}

/**
 * Get POS labels map for a specific locale
 * @param locale - Target locale
 * @returns POS labels map
 */
export function getPOSLabels(locale?: SupportedLocale): Record<string, string> {
  const targetLocale = locale || getCurrentLocale();
  return posLabels[targetLocale] || posLabels['zh-TW'];
}
